import React from "react";
import {
  CommunityHub,
  addHubModerator,
  getInitials,
  canBeAssignedAsHubModerator,
  normalizeEmail,
  removeHubModerator,
  useHubMembers,
  useHubModerators,
} from "../../../data/communityHub";
import useModal from "../../../hooks/useModal";
import ManageMembersModal from "./ManageMembersModal";
import ManageModeratorsModal from "./ManageModeratorsModal";
import { Settings } from "lucide-react";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import CommunityAvatar from "../shared/CommunityAvatar";

const MEMBER_GRID_MAX = 9;
const MEMBER_GRID_OVERFLOW_AT = 8;

function MemberChip({
  name,
  email,
  photoURL,
  size = 40,
}: {
  name: string;
  email?: string;
  photoURL?: string;
  size?: number;
}) {
  const firstName = (name || email || "?").split(" ")[0];

  return (
    <div className="flex flex-col items-center gap-0.5 text-center min-w-0">
      <CommunityAvatar
        initials={getInitials(name)}
        name={name}
        avatarUrl={photoURL}
        size={size}
      />
      <span className="text-[11px] text-gray-600 truncate max-w-full w-full px-0.5">
        {firstName}
      </span>
    </div>
  );
}

function MoreMembersChip({ count, size = 40 }: { count: number; size?: number }) {
  return (
    <div className="flex flex-col items-center gap-0.5 text-center min-w-0">
      <div
        className="rounded-full bg-[#E8EEF2] text-[#15415E] font-bold shrink-0 flex items-center justify-center leading-none"
        style={{ width: size, height: size, fontSize: 14 }}
        title={`${count} more members`}
      >
        +{count}
      </div>
      <span className="text-[11px] text-gray-600">more</span>
    </div>
  );
}

export default function CommunityHubManageSidebar({
  hub,
  canManage,
}: {
  hub: CommunityHub;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const membersModal = useModal();
  const moderatorModal = useModal();

  const { data: members = [] } = useHubMembers(hub.id);
  const { data: extraModerators = [] } = useHubModerators(hub.id);

  const primaryModeratorEmail = normalizeEmail(hub.moderatorBy?.email);
  const memberEmails = (members || []).map((m: any) => normalizeEmail(m.email));

  const refresh = () => {
    qc.invalidateQueries(["communityHub", hub.id]);
    qc.invalidateQueries(["communityHub"]);
    qc.invalidateQueries(["hubMembers", hub.id]);
    qc.invalidateQueries(["hubModerators", hub.id]);
  };

  const handleAddModerator = async (picked: {
    id: string;
    name: string;
    email: string;
    role: string;
  }) => {
    if (!canBeAssignedAsHubModerator(picked.role)) {
      toast.error("Only admins or seekers can be assigned as moderators");
      throw new Error("invalid role");
    }
    const email = normalizeEmail(picked.email);
    if (email === primaryModeratorEmail) {
      toast.error("Already the primary moderator");
      throw new Error("already primary");
    }
    if (extraModerators.some((m: any) => normalizeEmail(m.email) === email)) {
      toast.error("Already a moderator");
      throw new Error("already moderator");
    }
    await addHubModerator(hub.id, { email: picked.email, name: picked.name });
    refresh();
  };

  const allModeratorEmails = [
    primaryModeratorEmail,
    ...(extraModerators || []).map((m: any) => normalizeEmail(m.email)),
  ].filter(Boolean);

  const memberList = members || [];
  const hasMemberOverflow = memberList.length > MEMBER_GRID_MAX;
  const visibleMembers = hasMemberOverflow
    ? memberList.slice(0, MEMBER_GRID_OVERFLOW_AT)
    : memberList;
  const moreMemberCount = hasMemberOverflow
    ? memberList.length - MEMBER_GRID_OVERFLOW_AT
    : 0;

  return (
    <aside className="space-y-6 w-full">
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-[#15415E]">Manage</h3>
          {canManage && (
            <button
              type="button"
              onClick={membersModal.toggle}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-[#15415E] bg-[#F4F7F8] border border-gray-200 rounded-lg shrink-0 transition-colors duration-150 hover:bg-[#E4EBEF] hover:border-[#1E596D]/40 hover:text-[#1E596D]"
            >
              <Settings size={16} strokeWidth={2} />
              Manage
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-3">Members of this community</p>
        {memberList.length === 0 ? (
          <p className="text-sm text-gray-400">No members yet</p>
        ) : (
          <div className="grid grid-cols-3 gap-x-1 gap-y-2">
            {visibleMembers.map((member: any) => (
              <MemberChip
                key={member.id}
                name={member.name}
                email={member.email}
                photoURL={member.photoURL}
              />
            ))}
            {hasMemberOverflow && (
              <MoreMembersChip count={moreMemberCount} />
            )}
          </div>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-[#15415E]">Moderators</h3>
          {canManage && (
            <button
              type="button"
              onClick={moderatorModal.toggle}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-[#15415E] bg-[#F4F7F8] border border-gray-200 rounded-lg shrink-0 transition-colors duration-150 hover:bg-[#E4EBEF] hover:border-[#1E596D]/40 hover:text-[#1E596D]"
            >
              <Settings size={16} strokeWidth={2} />
              Manage
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Admins and seekers managing this community
        </p>
        <div className="space-y-3">
          {hub.moderatorBy && (
            <ModeratorRow
              name={hub.moderatorBy.name}
              email={hub.moderatorBy.email}
              isPrimary
            />
          )}
          {(extraModerators || []).map((mod: any) => (
            <ModeratorRow
              key={mod.id}
              name={mod.name}
              email={mod.email}
              canRemove={canManage}
              onRemove={async () => {
                await removeHubModerator(hub.id, mod.id);
                toast.success("1 moderator removed");
                refresh();
              }}
            />
          ))}
        </div>
      </section>

      {membersModal.isShowing && (
        <ManageMembersModal
          hubId={hub.id}
          hubName={hub.hubName}
          members={memberList}
          excludeEmails={[...memberEmails, ...allModeratorEmails]}
          onClose={membersModal.toggle}
          onSuccess={refresh}
        />
      )}

      {moderatorModal.isShowing && (
        <ManageModeratorsModal
          primaryModeratorName={hub.moderatorBy?.name}
          primaryModeratorEmail={hub.moderatorBy?.email}
          extraModerators={extraModerators || []}
          excludeEmails={allModeratorEmails}
          onClose={moderatorModal.toggle}
          onAddModerator={handleAddModerator}
          onSuccess={refresh}
        />
      )}

    </aside>
  );
}

function ModeratorRow({
  name,
  email,
  isPrimary,
  canRemove,
  onRemove,
}: {
  name: string;
  email?: string;
  isPrimary?: boolean;
  canRemove?: boolean;
  onRemove?: () => void;
}) {
  const displayName = name || email || "—";
  const showEmail =
    !!email && normalizeEmail(email) !== normalizeEmail(displayName);

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <CommunityAvatar
          initials={getInitials(displayName)}
          name={displayName}
          size={36}
        />
        <div className="min-w-0">
          <span className="text-sm font-medium text-[#15415E] truncate block">
            {displayName}
          </span>
          {showEmail && (
            <span className="text-xs text-gray-500 truncate block">
              {email}
            </span>
          )}
          {isPrimary && (
            <span className="text-[10px] text-gray-400 uppercase">Primary</span>
          )}
        </div>
      </div>
      {canRemove && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-red-500 hover:underline shrink-0"
        >
          Remove
        </button>
      )}
    </div>
  );
}
